import { useEffect, useRef } from 'react';

import { useAuth } from 'react-oidc-context';

const COOKIE_NAME = import.meta.env.VITE_SESSION_COOKIE_NAME ?? '';
const COOKIE_DOMAIN = import.meta.env.VITE_SESSION_COOKIE_DOMAIN ?? '';
const ALLOWED_DOMAINS = (import.meta.env.VITE_SESSION_ALLOWED_DOMAINS ?? '')
  .split(',')
  .map((domain) => domain.trim())
  .filter(Boolean);
const POLL_INTERVAL_MS = 2000;

function isSessionSyncSupported(): boolean {
  if (!COOKIE_NAME || !COOKIE_DOMAIN || ALLOWED_DOMAINS.length === 0) return false;
  const { hostname } = window.location;
  return ALLOWED_DOMAINS.some((suffix) => hostname.endsWith(suffix));
}

function setCookie(value: string): void {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
}

function clearCookie(): void {
  document.cookie = `${COOKIE_NAME}=; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function readCookie(): string | null {
  const match = new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`).exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Cross-tab logout sync across configured *.cucollaborate.com subdomains.
 *
 * Sets a shared cookie at the configured domain keyed to the signed-in user's
 * sub. Polls every POLL_INTERVAL_MS — if the cookie disappears or no longer
 * matches, the local session is torn down so the user gets re-prompted.
 *
 * Cleared on local sign-out via the UserManager's userSignedOut / userUnloaded
 * events, so any sibling tab at another allowed subdomain detects the logout
 * within ~2 seconds. Becomes a no-op on hosts that don't match any entry of
 * VITE_SESSION_ALLOWED_DOMAINS (e.g. localhost dev), since browsers silently
 * reject cross-domain cookie writes.
 */
export function useSessionSync(): void {
  const auth = useAuth();
  const sub = auth.user?.profile.sub;
  const isAuthed = auth.isAuthenticated;

  const removeUserRef = useRef(auth.removeUser);
  removeUserRef.current = auth.removeUser;

  useEffect(() => {
    if (!isSessionSyncSupported()) return;
    const offSignedOut = auth.events.addUserSignedOut(clearCookie);
    const offUnloaded = auth.events.addUserUnloaded(clearCookie);
    return () => {
      offSignedOut();
      offUnloaded();
    };
  }, [auth.events]);

  useEffect(() => {
    if (!isSessionSyncSupported()) return;
    if (!isAuthed || !sub) return;

    setCookie(sub);

    const id = window.setInterval(() => {
      if (readCookie() !== sub) {
        void removeUserRef.current().finally(() => {
          window.location.reload();
        });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [isAuthed, sub]);
}
